/**
 * Resolves a glyph name from `foodCategoryIcons.ts` (`CuisineIconName` /
 * `FoodCategoryIcon`) to its same-named `@lucide/vue` component.
 *
 * This is the only place that maps the shared glyph vocabulary onto
 * renderable components: views resolve category slugs through
 * `resolveCuisineIconName` / `resolveFoodCategoryIcon` first, then render
 * the component returned here. Unknown glyphs fall back to `Utensils` — the
 * same universal fallback the name resolvers use.
 */
import {
  Beef,
  Cake,
  Coffee,
  Croissant,
  EggFried,
  Fish,
  Flame,
  Leaf,
  LeafyGreen,
  Pizza,
  Salad,
  Sandwich,
  Shrimp,
  Soup,
  Utensils
} from '@lucide/vue'
import type { CuisineIconName } from './CuisineIconName'
import type { FoodCategoryIcon } from './FoodCategoryIcon'
import type { CuisineIconComponent } from './CuisineIconComponent'

type CuisineGlyphName = CuisineIconName | FoodCategoryIcon

const cuisineGlyphComponents: Readonly<Record<CuisineGlyphName, CuisineIconComponent>> = {
  beef: Beef,
  cake: Cake,
  coffee: Coffee,
  croissant: Croissant,
  'egg-fried': EggFried,
  fish: Fish,
  flame: Flame,
  leaf: Leaf,
  pizza: Pizza,
  salad: Salad,
  sandwich: Sandwich,
  shrimp: Shrimp,
  soup: Soup,
  utensils: Utensils,
  vegetarian: LeafyGreen
}

/** Resolves a glyph name to its Lucide component, falling back to `Utensils`. */
export function resolveCuisineIconComponent(glyph: string): CuisineIconComponent {
  return cuisineGlyphComponents[glyph as CuisineGlyphName] ?? Utensils
}
